import { useState, useEffect, useMemo } from "preact/hooks"
import { useDropzone } from "react-dropzone"
import { toast } from "./ui/use-toast"
import { Button } from "./ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog"
import { Download, Trash2, Upload, Loader2 } from "lucide-react"
import { format } from "date-fns"
import { capitalCase } from "change-case"
import { BrandFile, uploadBrandFiles, listBrandFiles, deleteBrandFile } from "../api/brand_files"

interface BrandFilesProps {
  fileSource: string
  className?: string
  onFilesChange?: () => void
}

function handleViewS3Uri(s3_uri: string) {
  return function handleClick(e: MouseEvent) {
    e.preventDefault()
    e.stopImmediatePropagation()
    window.open(`/api/_s3?s3_uri=${encodeURIComponent(s3_uri)}`, "_blank")
  }
}

function DeleteFileDialog({ file, onDelete }: { file: BrandFile; onDelete: () => void }) {
  async function handleConfirmDelete() {
    try {
      await deleteBrandFile(file.id)
      toast({
        title: "File Deleted",
        description: "The file has been deleted successfully.",
      })
      onDelete()
    } catch (error) {
      console.error("Error deleting file:", error)
      toast({
        title: "Delete Failed",
        description: "Failed to delete the file. Please try again.",
        variant: "destructive",
      })
    }
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          onClick={e => e.stopPropagation()}
          class="p-1.5 rounded-full transition-colors cursor-pointer hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4 text-red-500 hover:text-red-600" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently delete the file.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={(e: Event) => e.stopPropagation()}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e: Event) => {
              e.stopPropagation()
              handleConfirmDelete()
            }}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export function BrandFiles({ fileSource, className, onFilesChange }: BrandFilesProps) {
  const [files, setFiles] = useState<BrandFile[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  async function loadFiles() {
    if (!fileSource) return
    setLoading(true)
    try {
      const fileList = await listBrandFiles(fileSource)
      setFiles(fileList)
      onFilesChange?.()
    } catch (error) {
      console.error("Error loading files:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadFiles()
  }, [fileSource])

  async function handleUpload(uploadedFiles: File[]) {
    if (!fileSource || uploadedFiles.length === 0) return
    
    // Accept all file types for all sources
    const validFiles = uploadedFiles
    
    setUploading(true)
    try {
      const newFiles = await uploadBrandFiles(fileSource, validFiles)
      if (newFiles.length > 0) {
        toast({
          title: "Files Uploaded",
          description: `${newFiles.length} file${newFiles.length !== 1 ? 's' : ''} uploaded successfully.`,
        })
        await loadFiles()
      }
    } catch (error) {
      console.error("Error uploading files:", error)
      toast({
        title: "Upload Failed",
        description: error instanceof Error ? error.message : "Failed to upload files. Please try again.",
        variant: "destructive",
      })
    } finally {
      setUploading(false)
    }
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleUpload,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    // Accept all file types
  })

  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0
      return dateB - dateA
    })
  }, [files])

  return (
    <div className={`relative ${className || ""}`}>
      <div>
        {/* Large Upload Area */}
        <div
          {...(getRootProps() as any)}
          class={`border-2 border-dashed rounded-lg p-8 text-center transition-colors mb-6 cursor-pointer ${
            isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"
          }`}
        >
          <input {...getInputProps()} />
          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p class="text-lg font-medium text-gray-900 mb-2">
            {isDragActive ? "Drop files here" : "Drag & drop files"}
          </p>
          <Button
            onClick={open}
            disabled={uploading}
            variant="outline"
            class="flex items-center mx-auto"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Select Files
              </>
            )}
          </Button>
        </div>

        {/* Files List */}
        <div class="flex justify-between items-center my-4">
          <h3 class="text-lg font-semibold">Uploaded Files</h3>
        </div>
        {loading ? (
          <div class="w-full flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : sortedFiles.length === 0 ? (
          <div class="text-center text-gray-500 py-8">
            <p class="text-sm">No files uploaded yet.</p>
            <p class="text-xs mt-2">Click Upload or drag and drop files here.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 my-4">
            {sortedFiles.map((file) => (
              <div
                key={file.id}
                class="flex gap-2 items-center p-2 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors w-full cursor-pointer overflow-x-hidden"
                onClick={handleViewS3Uri(file.s3_uri)}
              >
                <Download className="h-4 w-4 flex-shrink-0 text-green-500 hover:text-green-600" />
                <div class="grow">
                  <span class="select-text py-1 text-nowrap pr-2 -ml-1 pl-1">
                    {file.name}
                  </span>
                  <div class="flex justify-between">
                    <span class="text-gray-500 text-sm">
                      {file.actor_name && capitalCase(file.actor_name)}
                    </span>
                    <span class="text-gray-500 text-sm text-nowrap">
                      {file.created_at && format(new Date(file.created_at), "PP")}
                    </span>
                  </div>
                </div>
                <DeleteFileDialog
                  file={file}
                  onDelete={loadFiles}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

